import { useId, type SelectHTMLAttributes } from 'react'

import estilos from './Formulario.module.css'

export type OpcionSelector = {
  valor: string
  etiqueta: string
  deshabilitada?: boolean
}

type PropsSelector = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'children'> & {
  etiqueta: string
  opciones: OpcionSelector[]
  /** Primera línea del desplegable cuando todavía no hay elección. */
  textoVacio?: string
  ayuda?: string
  error?: string
  obligatorio?: boolean
}

/**
 * Desplegable de un maestro.
 *
 * Todas las listas del sistema salen de las tablas maestras: cliente,
 * servicio, zona, colaborador. Nunca se escriben a mano dentro de una orden.
 */
export function Selector({
  etiqueta,
  opciones,
  textoVacio = 'Seleccione…',
  ayuda,
  error,
  obligatorio = false,
  className,
  ...resto
}: PropsSelector) {
  const id = useId()
  const idAyuda = `${id}-ayuda`

  const clases = [estilos.control, error ? estilos.conError : '', className ?? '']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={estilos.campo}>
      <label className={estilos.etiqueta} htmlFor={id}>
        {etiqueta}
        {obligatorio && (
          <span className={estilos.obligatorio} aria-hidden="true">
            *
          </span>
        )}
      </label>

      <select
        id={id}
        className={clases}
        required={obligatorio}
        aria-invalid={Boolean(error)}
        aria-describedby={error || ayuda ? idAyuda : undefined}
        {...resto}
      >
        <option value="">{textoVacio}</option>

        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor} disabled={opcion.deshabilitada}>
            {opcion.etiqueta}
          </option>
        ))}
      </select>

      {error ? (
        <span id={idAyuda} className={estilos.error}>
          {error}
        </span>
      ) : (
        ayuda && (
          <span id={idAyuda} className={estilos.ayuda}>
            {ayuda}
          </span>
        )
      )}
    </div>
  )
}
